<template>
  <v-dialog
    v-model="dialog"
    persistent
    width="600"
  >
    <template v-slot:activator="{ on, attrs }">
      <div v-bind="attrs" v-on="on">
        <slot />
      </div>
    </template>
    <v-card
      class="pa-6"
    >
      <h3 class="dialog-title">
        {{ $t('components.editor.imageInsertionDialog.formTitle') }}
      </h3>

      <v-form ref="form">
        <v-text-field
          v-show="!gotValidImage && !fromUploadedImage"
          v-model="url"
          :label="$t('components.editor.imageInsertionDialog.imageUrl')"
          :rules="urlRules"
          clearable
          class="url-field"
          ref="urlField"
        />

        <h4
          class="my-6"
          style="color: red; font-weight: 400;"
          v-if="showError === true"
        >
          {{ errorMessage() }}
        </h4>

        <v-img
          v-if="gotValidImage"
          class="image-preview"
          style="outline: 1px solid black;"
          :src="srcHref"
        />

        <v-btn
          v-if="gotValidImage"
          class="image-preview-discard-btn"
          icon
          @click="discardImage"
        >
          <v-icon v-if="gotValidImage">
            close
          </v-icon>
        </v-btn>

        <v-text-field
          v-if="gotValidImage && (fromUploadedImage === false || ready === true)"
          v-model="title"
          :label="$t('components.editor.imageInsertionDialog.imageTitle')"
          clearable
        />
      </v-form>
      <v-row
        no-gutters
        class="mt-2 justify-end"
      >
        <v-btn
          class="mr-2"
          small
          text
          color="primary"
          @click="cancel"
        >
          {{ $t('components.editor.imageInsertionDialog.cancelButtonLabel') }}
        </v-btn>
        <v-btn
          :disabled="!gotValidImage || uploadInProgress"
          small
          color="primary"
          @click="insertImage"
        >
          <div>
            <span>{{ insertButtonLabel() }}</span>
            <v-progress-linear
              v-if="uploadInProgress || uploadProgressState === 100"
              v-model="uploadProgressState"
            />
          </div>
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { types } from '../../../../../store/rootTypes';

export default {
  name: 'ImageInsertionDialog',
  components: {},
  props: {
    imagePreview: {
      type: String,
      required: false,
      default: undefined,
    },
    dz: {
      type: Object,
      default: undefined,
    },
    reflectionId: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      dialog: false,
      url: '',
      urlRules: [
        v => (v && v.length !== 0 && this.isValidUrl(v) && this.gotValidImage === true)
          || this.$t('components.editor.imageInsertionDialog.invalidUrlMessage'),
      ],
      title: '',
      imageWidth: undefined,
      imageHeight: undefined,
      validUrl: undefined,
      gotValidImage: false,
      uploadError: false,
      ready: false,
    };
  },
  computed: {
    ...mapGetters({
      uploadInProgress: types.upload.getters.uploadInProgress,
      uploadedFile: types.upload.getters.uploadedFile,
      uploadProgressState: types.upload.getters.uploadProgressState,
      uploadedFileDownloadUrl: types.upload.getters.uploadedFileDownloadUrl,
      uploadSuccess: types.upload.getters.uploadSuccess,
    }),
    srcHref() {
      if (this.validUrl !== undefined) {
        return this.validUrl.href;
      }

      if (this.imagePreview) {
        return this.imagePreview;
      }

      return '';
    },
    fromUploadedImage() {
      return this.imagePreview !== undefined;
    },
    needToUpload() {
      return this.fromUploadedImage && !this.uploadSuccess && !this.uploadError;
    },
    showError() {
      return this.ready === true && this.gotValidImage === false && this.fromUploadedImage === true;
    },
  },
  methods: {
    ...mapActions({
      uploadReflectionImage: types.upload.actions.uploadReflectionImage,
    }),
    ...mapMutations({
      resetUploadState: types.upload.mutations.resetUploadState,
    }),
    errorMessage() {
      if (this.uploadError) {
        return this.$t('components.editor.imageInsertionDialog.uploadErrorMessage');
      }
      return this.$t('components.editor.imageInsertionDialog.invalidImageMessage');
    },
    insertButtonLabel() {
      if (this.fromUploadedImage) {
        return this.$t('components.editor.imageInsertionDialog.insertUploadAndLinkButtonLabel');
      }
      return this.$t('components.editor.imageInsertionDialog.insertLinkButtonLabel');
    },
    async checkImage(imageSrc) {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          this.imageWidth = image.width;
          this.imageHeight = image.height;
          return resolve(true);
        };
        image.onerror = () => resolve(false);
        image.src = imageSrc;
      });
    },
    async uploadImage() {
      const files = this.dz.getQueuedFiles();

      if (files.length === 0) {
        throw new Error('No files to upload');
      }

      const file = files[0];
      const title = this.title || file.name;

      await this.uploadReflectionImage({
        file,
        title,
        reflectionId: this.reflectionId,
      });
      this.dz.removeAllFiles();

      return { title };
    },
    async insertImage() {
      this.resetUploadState();

      const { title } = await this.uploadImage();
      this.url = this.uploadedFileDownloadUrl;

      if (this.imageWidth > 736) {
        const scale = this.imageWidth / 736;
        this.imageWidth = 736;
        this.imageHeight /= scale;
      }

      this.$emit('insertImage', {
        src: this.url,
        alt: title,
        title,
        width: this.imageWidth,
        height: this.imageHeight,
      });

      this.resetState(this.fromUploadedImage);
    },
    image() {
      const files = this.dz.getQueuedFiles();

      if (files.length === 0) return null;

      return files[0];
    },
    discardImage() {
      this.resetState(this.fromUploadedImage);
      if (this.fromUploadedImage) {
        this.$emit('cancel');
      }
      setTimeout(() => this.$refs.urlField.focus(), 300);
    },
    resetState(closeDialog = true) {
      if (closeDialog) {
        this.dialog = false;
      }

      this.resetUploadState();
      this.url = '';
      this.title = '';
      this.validUrl = undefined;
      this.gotValidImage = false;
      this.uploadError = false;
      this.ready = false;
      this.$refs.form.reset();
    },
    cancel() {
      this.resetState();
      this.$emit('cancel');
    },
    isValidUrl(url) {
      try {
        const validatedUrl = new URL(url);
        this.validUrl = validatedUrl;
        return true;
      } catch (e) {
        return false;
      }
    },
  },
  watch: {
    dialog(isOpened) {
      if (isOpened) {
        setTimeout(() => this.$refs.urlField.focus(), 300);
        const image = this.image();

        if (image) {
          this.title = image.name;
        }
      }
    },
    async imagePreview(newVal) {
      this.gotValidImage = await this.checkImage(newVal);
      this.ready = true;
    },
    uploadedFile(file) {
      if (file instanceof Error) {
        this.gotValidImage = false;
        this.uploadError = true;
      }
    },
    async validUrl(url) {
      if (!url) return;
      this.gotValidImage = await this.checkImage(url.href);
    },
  },
};
</script>

<style>
.dialog-title {
  font-size: 18px;
  font-weight: 500;
}
.image-preview {
  padding: 8px;
  margin: 16px 0px;
  position: relative;
}
.image-preview-discard-btn {
  position: absolute;
  top: 75px;
  right: 30px;
  background-color: #eee;
  border: 1px solid #aaa;
}
</style>
